import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Logo, Shield, Sparkles } from '../components/Icons.jsx';
import { Intro, Done, mainStyle } from './VennArchitect.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';

// Generic SJT runner. `bank` is an array of items, each with one of these shapes:
//   appropriateness: { kind: 'appropriateness', scenario, option, answer (1-4), working }
//   match:           { kind: 'match', scenario, options: [{ label, value }], answer (string), working }
// The runner picks `length` items at random per round, shows them one at a time, scores partial-credit for appropriateness and exact for match.
export default function SjtRunner({ title, intro, slug, banks, defaultLength = 10, kicker }) {
  const [stage, setStage] = useState('intro');
  const [mode, setMode] = useState(Object.keys(banks)[0]);
  const [length, setLength] = useState(defaultLength);
  const [ucatsen, setUcatsen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);

  const pool = banks[mode];
  const questions = useMemo(() => {
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(length, pool.length));
  }, [pool, length, stage]);

  const q = questions[idx];

  const start = () => { setIdx(0); setAnswers([]); setPicked(null); setStage('drill'); };
  const next = () => {
    let score = 0;
    if (q.kind === 'appropriateness') {
      const delta = Math.abs(picked - q.answer);
      score = Math.max(0, 3 - delta);
    } else {
      score = picked === q.answer ? 3 : 0;
    }
    setAnswers([...answers, { ...q, given: picked, score, maxScore: 3 }]);
    setPicked(null);
    if (idx < questions.length - 1) setIdx(idx + 1);
    else setStage('done');
  };

  const totalScore = answers.reduce((s, a) => s + a.score, 0);
  const maxScore = answers.length * 3;
  const overallPct = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={drillHeader}>
        <Link to="/practice" style={drillExit}><ArrowLeft width="16" height="16" />Exit</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight /><strong style={titleStyle}>{title}</strong>
        </div>
        <div style={{ width: 60 }} />
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="SJT" title={title} body={intro}
          how={['Read each scenario carefully', 'Rate the option or pick a category', 'Partial credit where applicable']}
          difficulty={mode} setDifficulty={setMode}
          difficultyOptions={Object.keys(banks).map((k) => ({ v: k, label: banks[k]._meta?.label || k, sub: banks[k]._meta?.sub || '' }))}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
          extra={
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Length</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[5, 10, Math.min(15, pool.length)].filter((n, i, a) => a.indexOf(n) === i).map((n) => (
                  <button key={n} onClick={() => setLength(n)} style={lengthBtn(length === n)}>{n} questions</button>
                ))}
              </div>
            </div>
          }
        />
      )}

      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600 }}>Question {idx + 1} of {questions.length}</div>
          <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, marginBottom: 20 }}>
            <div style={{ width: `${((idx + 1) / questions.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', borderRadius: 99 }} />
          </div>

          <div className="card" style={{ marginBottom: 16, background: 'var(--card-soft)' }}>
            <div style={kickerStyle}>Scenario</div>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55 }}>{q.scenario}</p>
          </div>

          {q.kind === 'appropriateness' ? (
            <div className="card" style={{ marginBottom: 18 }}>
              <div style={kickerAccent}>Rate this option</div>
              <p style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600 }}>"{q.option}"</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { v: 1, label: 'Very appropriate' },
                  { v: 2, label: 'Appropriate, not ideal' },
                  { v: 3, label: 'Inappropriate, not awful' },
                  { v: 4, label: 'Very inappropriate' }
                ].map((r) => (
                  <button key={r.v} onClick={() => setPicked(r.v)} style={pickBtn(picked === r.v, r.v)}>
                    <span style={badge(picked === r.v)}>{r.v}</span>{r.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginBottom: 18 }}>
              <div style={kickerAccent}>Which category?</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                {q.options.map((opt) => (
                  <button key={opt.value} onClick={() => setPicked(opt.value)} style={pickBtn(picked === opt.value)}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button onClick={next} disabled={picked === null} className="btn btn-primary" style={{ opacity: picked === null ? 0.5 : 1 }}>
            {idx === questions.length - 1 ? 'Finish' : 'Next'} <ArrowRight width="14" height="14" />
          </button>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="SJT" title={title} stats={[
          { label: 'Score', value: `${totalScore} / ${maxScore}` },
          { label: 'Accuracy', value: `${overallPct}%` },
          { label: 'Mode', value: banks[mode]._meta?.label || mode }
        ]} onRestart={start}
          extra={<>
            <DrillResultBar slug={slug} score={totalScore} maxScore={maxScore} accuracyPct={overallPct} />
            {overallPct < 70 && (
              <TutorNudge tutor={tutorFor('sjt')} reason={`${overallPct}% is below the band most schools accept.`} kicker={kicker || 'SJT marks leaking'} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18, marginBottom: 22 }}>
              {answers.map((a, i) => (
                <div key={i} className="card" style={{ borderLeft: `4px solid ${a.score === 3 ? 'var(--live-green)' : a.score >= 1 ? '#b45309' : '#b91c1c'}` }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Q{i + 1} · {a.score}/3</div>
                  <p style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--text-2)' }}>{a.scenario}</p>
                  {a.kind === 'appropriateness' ? (
                    <p style={{ margin: '4px 0 6px', fontSize: 14, fontWeight: 600 }}>"{a.option}"</p>
                  ) : null}
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Your: <strong>{a.given}</strong> · Correct: <strong>{a.answer}</strong>
                  </div>
                  {a.working && <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-2)', padding: '6px 10px', background: 'var(--card-soft)', borderRadius: 6 }}>{a.working}</p>}
                </div>
              ))}
            </div>
          </>}
        />
      )}
    </div>
  );
}

const drillHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)' };
const drillExit = { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 };
const titleStyle = { fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' };
const kickerStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 };
const kickerAccent = { fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 };

function pickBtn(active, v) {
  return { padding: '12px 16px', textAlign: 'left', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', border: `2px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`, background: active ? 'var(--accent-soft)' : '#fff', color: 'var(--text)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 };
}
function badge(active) {
  return { width: 24, height: 24, borderRadius: '50%', background: active ? 'var(--brand-purple)' : 'var(--card-soft)', color: active ? '#fff' : 'var(--text-2)', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 };
}
function lengthBtn(active) {
  return { padding: '8px 16px', borderRadius: 99, cursor: 'pointer', fontSize: 14, fontWeight: 600, border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`, background: active ? 'var(--accent-soft)' : '#fff', color: active ? 'var(--brand-deep)' : 'var(--text-2)' };
}
